import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tabs,
  Tab,
  Table,
  TableHead,
  
  TableRow,
  TableCell,
  TableBody,
  AlertTitle,
  Pagination,
  LinearProgress,
  Grid
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  EventNote as EventNoteIcon,
  Visibility as VisibilityIcon,
  ArchiveOutlined as ArchiveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Services
import { listarPropostas, aprovarProposta, rejeitarProposta, obterSimilaridade } from '../../services/propostaService';
import { listarProjetos, aprovarProjeto, obterSimilaridadeProjeto } from '../../services/projetoService';
import { listarMonografias, obterSimilaridadeMonografia } from '../../services/monografiaService';
import CientificoDashboardService from '../../services/CientificoDashboardService';

// Componentes já existentes
import DefesaList from '../../components/Defesa/DefesaList';
import ArquivarMonografia from '../../components/Monografia/ArquivarMonografia';

function CientificoDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // ---------------------------
  // ESTADOS GERAIS
  // ---------------------------
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------------------------
  // ESTADOS PARA PROPOSTAS
  // ---------------------------
  const [propostas, setPropostas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProposta, setSelectedProposta] = useState(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [similaridades, setSimilaridades] = useState({});

  // Diálogo de aprovação de Proposta
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [observacoesProposta, setObservacoesProposta] = useState('');

  const [filtroStatusProposta, setFiltroStatusProposta] = useState('PENDENTE');
  const [pagePropostas, setPagePropostas] = useState(1);
  const pageSizePropostas = 5;

  // ---------------------------
  // ESTADOS PARA PROJETOS
  // ---------------------------
  const [projetos, setProjetos] = useState([]);
  const [observacoesProjeto, setObservacoesProjeto] = useState('');
  const [projetoDialogOpen, setProjetoDialogOpen] = useState(false);
  const [selectedProjeto, setSelectedProjeto] = useState(null);
  const [similaridadesProjetos, setSimilaridadesProjetos] = useState({});

  const [filtroStatus, setFiltroStatus] = useState('PENDENTE');
  const [pageProjetos, setPageProjetos] = useState(1);
  const pageSizeProjetos = 5;

  // ---------------------------
  // ESTADOS PARA MONOGRAFIAS
  // ---------------------------
  const [monografias, setMonografias] = useState([]);
  const [similaridadesMonografias, setSimilaridadesMonografias] = useState({});

  // ---------------------------
  // FUNÇÕES PARA CARREGAR SIMILARIDADES
  // ---------------------------
  const carregarSimilaridades = async (propostasCarregadas) => {
    try {
      console.log('Iniciando carregamento de similaridades (Propostas)');
      const novasSimilaridades = {};

      for (const proposta of propostasCarregadas) {
        if (proposta.status === 'PENDENTE') {
          console.log(`Buscando similaridade para proposta ${proposta.id}:`, proposta);
          try {
            const similaridade = await obterSimilaridade(proposta.id);
            if (similaridade.length > 0) {
              const dadosFormatados = similaridade.map((s) => ({
                propostaId: s.propostaId,
                similaridadeDireta: parseFloat(s.similaridadeDireta || 0),
                jaroWinkler: parseFloat(s.jaroWinkler || 0),
                similaridade: parseFloat(s.similaridade || 0),
                termosComuns: Array.isArray(s.termosComuns) ? s.termosComuns : [],
                propostaOriginal: {
                  titulo: s.propostaOriginal?.titulo || 'Sem título',
                  problemaPesquisa: s.propostaOriginal?.problemaPesquisa || 'Sem problema',
                  estudante: s.propostaOriginal?.estudante || 'Sem estudante',
                  dataSubmissao: s.propostaOriginal?.dataSubmissao || null
                }
              }));
              novasSimilaridades[proposta.id] = dadosFormatados;
            }
          } catch (err) {
            console.error(`Erro ao carregar similaridade da proposta ${proposta.id}:`, err);
          }
        }
      }
      console.log('Similaridades formatadas (Propostas):', novasSimilaridades);
      setSimilaridades(novasSimilaridades);
    } catch (error) {
      console.error('Erro ao carregar similaridades (Propostas):', error);
    }
  };

  const carregarSimilaridadesProjetos = async (projetosCarregados) => {
    try {
      console.log('Iniciando carregamento de similaridades (Projetos)');
      const novasSimilaridades = {};
      for (const projeto of projetosCarregados) {
        if (projeto.status === 'PENDENTE' && projeto.documento_projeto) {
          console.log(`Buscando similaridade para projeto ${projeto.id}`);
          try {
            const similaridade = await obterSimilaridadeProjeto(projeto.id);
            if (similaridade.length > 0) {
              novasSimilaridades[projeto.id] = similaridade;
            }
          } catch (err) {
            console.error(`Erro ao carregar similaridade do projeto ${projeto.id}:`, err);
          }
        }
      }
      console.log('Similaridades formatadas (Projetos):', novasSimilaridades);
      setSimilaridadesProjetos(novasSimilaridades);
    } catch (error) {
      console.error('Erro ao carregar similaridades (Projetos):', error);
    }
  };

  const carregarSimilaridadesMonografias = async (monografiasCarregadas) => {
    try {
      console.log('Iniciando carregamento de similaridades (Monografias)');
      const novasSimilaridades = {};
      for (const monografia of monografiasCarregadas) {
        if (['PENDENTE', 'SUBMETIDA', 'EM_REVISAO'].includes(monografia.status)) {
          console.log(`Buscando similaridade para monografia ${monografia.id}`);
          try {
            const similaridade = await obterSimilaridadeMonografia(monografia.id);
            if (similaridade.length > 0) {
              novasSimilaridades[monografia.id] = similaridade;
            }
          } catch (err) {
            console.error(`Erro ao carregar similaridade da monografia ${monografia.id}:`, err);
          }
        }
      }
      console.log('Similaridades formatadas (Monografias):', novasSimilaridades);
      setSimilaridadesMonografias(novasSimilaridades);
    } catch (error) {
      console.error('Erro ao carregar similaridades (Monografias):', error);
    }
  };

  // ---------------------------
  // FUNÇÃO GERAL PARA CARREGAR DADOS
  // ---------------------------
  const carregarDados = async () => {
    try {
      setLoading(true);
      if (activeTab === 0) {
        // Propostas
        const response = await listarPropostas();
        setPropostas(response.data);
        await carregarSimilaridades(response.data);
      } else if (activeTab === 1) {
        // Projetos
        const response = await listarProjetos();
        setProjetos(response.data);
        await carregarSimilaridadesProjetos(response.data);
      } else if (activeTab === 2 || activeTab === 5) {
        // Monografias ou aba de Arquivamento
        const responseMonografias = await listarMonografias();
        
        // Se estamos na aba de Arquivamento, buscar também os arquivos digitais
        if (activeTab === 5) {
          try {
            // Importar o serviço
            const { listarArquivosDigitais } = require('../../services/arquivoDigitalService');
            const responseArquivos = await listarArquivosDigitais();
            
            // Mapa de IDs de monografias arquivadas
            const monografiasArquivadas = {};
            responseArquivos.forEach(arquivo => {
              if (arquivo.monografia_id) {
                monografiasArquivadas[arquivo.monografia_id] = true;
              }
            });
            
            // Atribuir a flag arquivada às monografias
            const monografiasProcessadas = responseMonografias.data.map(monografia => ({
              ...monografia,
              arquivada: !!monografiasArquivadas[monografia.id]
            }));
            
            setMonografias(monografiasProcessadas);
          } catch (error) {
            console.error("Erro ao buscar arquivos digitais:", error);
            setMonografias(responseMonografias.data);
          }
        } else {
          setMonografias(responseMonografias.data);
        }
        
        if (activeTab === 2) {
          await carregarSimilaridadesMonografias(responseMonografias.data);
        }
      }
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Não foi possível carregar os dados. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ---------------------------
  // EFEITO ADICIONAL: Processar monografias para definir flag "arquivada"
  // ---------------------------
  //useEffect(() => {
    ///if (monografias.length > 0) {
      //const monografiasProcessadas = monografias.map(monografia => {
        //if (typeof monografia.arquivada === 'undefined') {
         // const arquivada = !!monografia.arquivo_digital_id;
          //return { ...monografia, arquivada };
       // }
        //return monografia;
      //});
      ///setMonografias(monografiasProcessadas);
      ///console.log("Monografias processadas para arquivamento:", monografiasProcessadas);
   /// }
  ///}, [monografias]);

  // ---------------------------
  // HANDLERS DE PROPOSTAS
  // ---------------------------
  const openApproveDialog = (proposta) => {
    setSelectedProposta(proposta);
    setApproveDialogOpen(true);
  };

  const handleAprovarPropostaConfirm = async () => {
    try {
      await aprovarProposta(selectedProposta.id, observacoesProposta);
      setApproveDialogOpen(false);
      setObservacoesProposta('');
      setSelectedProposta(null);
      await carregarDados();
    } catch (err) {
      console.error('Erro ao aprovar proposta:', err);
      setError('Erro ao aprovar proposta. Por favor, tente novamente.');
    }
  };

  const openRejectDialog = (proposta) => {
    setSelectedProposta(proposta);
    setDialogOpen(true);
  };

  const handleRejeitarProposta = async () => {
    try {
      await rejeitarProposta(selectedProposta.id, motivoRejeicao);
      setDialogOpen(false);
      setMotivoRejeicao('');
      setSelectedProposta(null);
      await carregarDados();
    } catch (err) {
      console.error('Erro ao rejeitar proposta:', err);
      setError('Erro ao rejeitar proposta. Por favor, tente novamente.');
    }
  };

  // ---------------------------
  // HANDLERS DE PROJETOS
  // ---------------------------
  const openApproveProjetoDialog = (projeto) => {
    setSelectedProjeto(projeto);
    setProjetoDialogOpen(true);
  };

  const handleAprovarProjeto = async () => {
    try {
      setLoading(true);
      await aprovarProjeto(selectedProjeto.id, observacoesProjeto);
      await carregarDados();
      setProjetoDialogOpen(false);
      setObservacoesProjeto('');
      setSelectedProjeto(null);
    } catch (err) {
      console.error('Erro ao aprovar projeto:', err);
      setError('Erro ao aprovar projeto. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // PAGINAÇÃO E FILTRO PARA PROPOSTAS
  // ---------------------------
  const propostasFiltradas = useMemo(() => {
    if (!filtroStatusProposta) return propostas;
    return propostas.filter((p) => p.status === filtroStatusProposta);
  }, [propostas, filtroStatusProposta]);

  const totalPropostas = propostasFiltradas.length;
  const totalPagesPropostas = Math.ceil(totalPropostas / pageSizePropostas);
  const startIndexPropostas = (pagePropostas - 1) * pageSizePropostas;
  const propostasPagina = propostasFiltradas.slice(startIndexPropostas, startIndexPropostas + pageSizePropostas);

  useEffect(() => {
    if (pagePropostas > totalPagesPropostas && totalPagesPropostas > 0) {
      setPagePropostas(totalPagesPropostas);
    }
  }, [pagePropostas, totalPagesPropostas]);

  // ---------------------------
  // PAGINAÇÃO E FILTRO PARA PROJETOS
  // ---------------------------
  const projetosFiltrados = useMemo(() => {
    if (!filtroStatus) return projetos;
    return projetos.filter((p) => p.status === filtroStatus);
  }, [projetos, filtroStatus]);

  const totalProjetos = projetosFiltrados.length;
  const totalPagesProjetos = Math.ceil(totalProjetos / pageSizeProjetos);
  const startIndexProjetos = (pageProjetos - 1) * pageSizeProjetos;
  const projetosPagina = projetosFiltrados.slice(startIndexProjetos, startIndexProjetos + pageSizeProjetos);

  useEffect(() => {
    if (pageProjetos > totalPagesProjetos && totalPagesProjetos > 0) {
      setPageProjetos(totalPagesProjetos);
    }
  }, [pageProjetos, totalPagesProjetos]);

  const handleChangePageProjetos = (_, newPage) => {
    setPageProjetos(newPage);
  };

  // ---------------------------
  // ABA "PROPOSTAS"
  // ---------------------------
  const renderPropostas = () => (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography variant="h6">Administrar Propostas</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={filtroStatusProposta === 'PENDENTE' ? 'contained' : 'outlined'}
                onClick={() => {
                  setFiltroStatusProposta('PENDENTE');
                  setPagePropostas(1);
                }}
                size="small"
              >
                Pendentes
              </Button>
              <Button
                variant={filtroStatusProposta === 'APROVADA' ? 'contained' : 'outlined'}
                onClick={() => {
                  setFiltroStatusProposta('APROVADA');
                  setPagePropostas(1);
                }}
                size="small"
              >
                Aprovadas
              </Button>
            </Box>
          </Box>

          {propostasFiltradas.length === 0 ? (
            <Alert severity="info">
              Não há propostas {filtroStatusProposta === 'PENDENTE' ? 'pendentes' : 'aprovadas'} de avaliação.
            </Alert>
          ) : (
            <>
              <Box sx={{ mt: 3 }}>
                {propostasPagina.map((proposta) => (
                  <Paper
                    key={proposta.id}
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {proposta.titulo}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          <strong>Problema:</strong> {proposta.problemaPesquisa}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Estudante:</strong> {proposta.estudante?.nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Submetida em:</strong>{' '}
                          {new Date(proposta.dataSubmissao).toLocaleDateString()}
                        </Typography>
                        <Chip
                          label={proposta.status}
                          color={proposta.status === 'PENDENTE' ? 'warning' : 'success'}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '150px' }}>
                        {proposta.status === 'PENDENTE' && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<CheckIcon />}
                              onClick={() => openApproveDialog(proposta)}
                            >
                              Aprovar
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              startIcon={<CloseIcon />}
                              onClick={() => openRejectDialog(proposta)}
                            >
                              Rejeitar
                            </Button>
                          </>
                        )}
                        {proposta.status === 'APROVADO' && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/propostas/${proposta.id}`)}
                          >
                            Ver Detalhes
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {proposta.status === 'PENDENTE' && similaridades[proposta.id] && (
                      <Box sx={{ mt: 3, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                          Análise de Similaridade
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>Proposta Atual:</strong>
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                            <li><strong>Título:</strong> {proposta.titulo}</li>
                            <li><strong>Problema de Pesquisa:</strong> {proposta.problemaPesquisa}</li>
                            <li><strong>Estudante:</strong> {proposta.estudante?.nome}</li>
                            <li>
                              <strong>Data de Submissão:</strong>{' '}
                              {new Date(proposta.dataSubmissao).toLocaleDateString()}
                            </li>
                          </ul>
                        </Box>

                        {similaridades[proposta.id].length > 0 && (
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                              <Paper
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  bgcolor:
                                    (similaridades[proposta.id][0]?.similaridadeDireta || 0) >= 0.6
                                      ? 'error.lighter'
                                      : 'background.default',
                                }}
                              >
                                <Typography variant="h6">
                                  {((similaridades[proposta.id][0]?.similaridadeDireta || 0) * 100).toFixed(1)}%
                                </Typography>
                                <Typography variant="caption">Similaridade Direta</Typography>
                              </Paper>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Paper
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  bgcolor:
                                    (similaridades[proposta.id][0]?.jaroWinkler || 0) >= 0.6
                                      ? 'error.lighter'
                                      : 'background.default',
                                }}
                              >
                                <Typography variant="h6">
                                  {similaridades[proposta.id][0]?.jaroWinkler
                                    ? `${(similaridades[proposta.id][0].jaroWinkler * 100).toFixed(1)}%`
                                    : '0%'}
                                </Typography>
                                <Typography variant="caption">Similaridade Jaro-Winkler</Typography>
                              </Paper>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                              <Paper
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  bgcolor:
                                    (similaridades[proposta.id][0]?.similaridade || 0) >= 0.6
                                      ? 'error.lighter'
                                      : 'background.default',
                                }}
                              >
                                <Typography variant="h6">
                                  {((similaridades[proposta.id][0]?.similaridade || 0) * 100).toFixed(1)}%
                                </Typography>
                                <Typography variant="caption">Score Combinado</Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                        )}

                        {similaridades[proposta.id]?.[0]?.similaridade >= 0.6 && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            <AlertTitle>Atenção - Alta Similaridade Detectada</AlertTitle>
                            <Typography variant="body2">
                              {`O problema de pesquisa "${proposta.problemaPesquisa}" apresenta ${
                                (similaridades[proposta.id][0].similaridade * 100).toFixed(1)
                              }% de similaridade com outra proposta existente.${
                                similaridades[proposta.id][0].termosComuns?.length > 0
                                  ? ` Os termos em comum são: ${similaridades[proposta.id][0].termosComuns.join(', ')}.`
                                  : ''
                              } Recomenda-se uma análise detalhada antes da aprovação.`}
                            </Typography>
                          </Alert>
                        )}

                        {similaridades[proposta.id].length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Propostas Similares Encontradas:
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Título</TableCell>
                                  <TableCell>Problema de Pesquisa</TableCell>
                                  <TableCell>Estudante</TableCell>
                                  <TableCell>Data de Submissão</TableCell>
                                  <TableCell align="right">Grau de Similaridade</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {similaridades[proposta.id].map((similar) => (
                                  <TableRow key={similar.propostaId}>
                                    <TableCell>
                                      {similar.propostaOriginal?.titulo || 'Título indisponível'}
                                    </TableCell>
                                    <TableCell>
                                      {similar.propostaOriginal?.problemaPesquisa || 'Problema não disponível'}
                                    </TableCell>
                                    <TableCell>
                                      {similar.propostaOriginal?.estudante || '—'}
                                    </TableCell>
                                    <TableCell>
                                      {similar.propostaOriginal?.dataSubmissao
                                        ? new Date(similar.propostaOriginal.dataSubmissao).toLocaleDateString()
                                        : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {typeof similar.similaridade === 'number'
                                        ? `${(similar.similaridade * 100).toFixed(1)}%`
                                        : 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>

              {totalPagesPropostas > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    color="primary"
                    count={totalPagesPropostas}
                    page={pagePropostas}
                    onChange={(_, newPage) => setPagePropostas(newPage)}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // ---------------------------
  // ABA "PROJETOS"
  // ---------------------------
  const renderProjetos = () => (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography variant="h6">Administrar Projectos</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={filtroStatus === 'PENDENTE' ? 'contained' : 'outlined'}
                onClick={() => {
                  setFiltroStatus('PENDENTE');
                  setPageProjetos(1);
                }}
                size="small"
              >
                Pendentes
              </Button>
              <Button
                variant={filtroStatus === 'APROVADO' ? 'contained' : 'outlined'}
                onClick={() => {
                  setFiltroStatus('APROVADO');
                  setPageProjetos(1);
                }}
                size="small"
              >
                Aprovados
              </Button>
            </Box>
          </Box>
        </Box>
        {projetosFiltrados.length === 0 ? (
          <Alert severity="info">Não há projetos para exibir.</Alert>
        ) : (
          <>
            <Box sx={{ mt: 3 }}>
              {projetosPagina.map((projeto) => (
                <Paper
                  key={projeto.id}
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {projeto.proposta?.titulo}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Estudante:</strong> {projeto.proposta?.estudante?.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Supervisor:</strong> {projeto.supervisor?.Usuario?.nome}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Submetido em:</strong>{' '}
                        {new Date(projeto.created_at).toLocaleDateString()}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip
                          label={projeto.status}
                          color={projeto.status === 'APROVADO' ? 'success' : 'warning'}
                          size="small"
                        />
                        {projeto.status === 'APROVADO' && (
                          <Chip
                            label={`Aprovado em: ${new Date(projeto.updated_at).toLocaleDateString()}`}
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                      {projeto.observacoes && projeto.status === 'APROVADO' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Observações:</strong> {projeto.observacoes}
                        </Typography>
                      )}
                      {projeto.status === 'PENDENTE' && similaridadesProjetos[projeto.id] && similaridadesProjetos[projeto.id].length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <AlertTitle>Alerta de Similaridade</AlertTitle>
                          <Typography variant="body2">
                            Este projeto apresenta {(similaridadesProjetos[projeto.id][0].similaridade * 100).toFixed(1)}% 
                            de similaridade com outro projeto existente.
                            {similaridadesProjetos[projeto.id][0].termosComuns?.length > 0 && 
                              ` Termos em comum: ${similaridadesProjetos[projeto.id][0].termosComuns.slice(0, 5).join(', ')}...`
                            }
                          </Typography>
                          <Button 
                            size="small" 
                            sx={{ mt: 1 }} 
                            onClick={() => navigate(`/projetos/${projeto.id}`)}
                          >
                            Ver detalhes
                          </Button>
                        </Alert>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '150px' }}>
                      {projeto.status === 'PENDENTE' && (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={() => openApproveProjetoDialog(projeto)}
                        >
                          Aprovar
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/projetos/${projeto.id}`)}
                      >
                        Ver Detalhes
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
            {totalPagesProjetos > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  color="primary"
                  count={totalPagesProjetos}
                  page={pageProjetos}
                  onChange={handleChangePageProjetos}
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  // ---------------------------
  // ABA "MONOGRAFIAS"
  // ---------------------------
  const renderMonografias = () => (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">Administrar Monografias</Typography>
        </Box>
        {monografias.length === 0 ? (
          <Alert severity="info">Não há monografias disponíveis no momento.</Alert>
        ) : (
          monografias.map((monografia) => (
            <Paper
              key={monografia.id}
              elevation={0}
              sx={{
                p: 3,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {monografia.titulo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Estudante:</strong> {monografia.estudante?.nome}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong> {monografia.status}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '150px' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/monografias/${monografia.id}`)}
                  >
                    Ver Detalhes
                  </Button>
                </Box>
              </Box>
              {['PENDENTE', 'SUBMETIDA', 'EM_REVISAO'].includes(monografia.status) && 
               similaridadesMonografias[monografia.id] &&
               similaridadesMonografias[monografia.id][0]?.similaridade >= 0.5 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <AlertTitle>Alerta de Similaridade</AlertTitle>
                  <Typography variant="body2">
                    Esta monografia apresenta {(similaridadesMonografias[monografia.id][0].similaridade * 100).toFixed(1)}% 
                    de similaridade com {similaridadesMonografias[monografia.id][0].tipo === 'arquivo_digital' ? 'um documento do repositório' : 'outra monografia'}.
                    {similaridadesMonografias[monografia.id][0].termosComuns?.length > 0 && 
                      ` Termos em comum: ${similaridadesMonografias[monografia.id][0].termosComuns.slice(0, 5).join(', ')}...`
                    }
                  </Typography>
                  <Button 
                    size="small" 
                    sx={{ mt: 1 }} 
                    onClick={() => navigate(`/monografias/${monografia.id}`)}
                  >
                    Ver detalhes
                  </Button>
                </Alert>
              )}
            </Paper>
          ))
        )}
      </CardContent>
    </Card>
  );

  // ---------------------------
  // ABA "ARQUIVAMENTO" INTEGRADA
  // ---------------------------
  const RenderArquivamento = () => {
    const [filtroStatusArquivamento, setFiltroStatusArquivamento] = useState('PENDENTE');
    
    // Filtrar monografias aprovadas e que ainda não foram arquivadas
    const monografiasArquivaveis = monografias.filter(
      (m) => m.status === 'APROVADA' && !m.arquivada
    );
    
    // Filtrar monografias que já foram arquivadas
    const monografiasArquivadas = monografias.filter(
      (m) => m.arquivada === true
    );
    
    // Define qual lista usar conforme o filtro selecionado
    const monografiasFiltradas =
      filtroStatusArquivamento === 'PENDENTE'
        ? monografiasArquivaveis
        : monografiasArquivadas;
  
    return (
      <Card>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}
            >
              <Typography variant="h6">
                Administrar Arquivamento de Monografias
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={filtroStatusArquivamento === 'PENDENTE' ? 'contained' : 'outlined'}
                  onClick={() => setFiltroStatusArquivamento('PENDENTE')}
                  size="small"
                >
                  Pendentes de Arquivamento
                </Button>
                <Button
                  variant={filtroStatusArquivamento === 'ARQUIVADA' ? 'contained' : 'outlined'}
                  onClick={() => setFiltroStatusArquivamento('ARQUIVADA')}
                  size="small"
                >
                  Arquivadas
                </Button>
              </Box>
            </Box>
  
            {/* Botão de recarga manual */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                onClick={() => carregarDados()}
                size="small"
              >
                Atualizar Dados
              </Button>
            </Box>
  
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {filtroStatusArquivamento === 'PENDENTE'
                  ? "Monografias aprovadas que podem ser arquivadas no repositório digital."
                  : "Monografias que já foram arquivadas no repositório digital."}
              </Typography>
            </Alert>
  
            {monografiasFiltradas.length === 0 ? (
              <Alert severity="info">
                {filtroStatusArquivamento === 'PENDENTE'
                  ? "Não há monografias pendentes de arquivamento."
                  : "Não há monografias arquivadas no repositório."}
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {monografiasFiltradas.map((monografia) => (
                  <Grid item xs={12} md={6} key={monografia.id}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)'
                        }
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {monografia.titulo || `Monografia #${monografia.id}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Estudante:</strong> {monografia.estudante?.nome}
                        </Typography>
                        {monografia.projeto?.supervisor?.Usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Supervisor:</strong> {monografia.projeto.supervisor.Usuario.nome}
                          </Typography>
                        )}
                        {monografia.defesa?.data_defesa && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Data da Defesa:</strong> {new Date(monografia.defesa.data_defesa).toLocaleDateString()}
                          </Typography>
                        )}
                        {filtroStatusArquivamento === 'ARQUIVADA' && monografia.data_arquivamento && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Arquivada em:</strong> {new Date(monografia.data_arquivamento).toLocaleDateString()}
                          </Typography>
                        )}
                        <Chip
                          label={monografia.status}
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>
  
                      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/monografias/${monografia.id}`)}
                        >
                          Ver Detalhes
                        </Button>
  
                        {filtroStatusArquivamento === 'PENDENTE' && (
                          <ArquivarMonografia
                            monografia={monografia}
                            onSuccess={() => {
                              console.log("Callback onSuccess chamado - recarregando dados");
                              carregarDados();
                            }}
                          />
                        )}
  
  {filtroStatusArquivamento === 'ARQUIVADA' && (
  <Button
    variant="outlined"
    color="primary"
    size="small"
    onClick={async () => {
      try {
        // Importar o serviço diretamente
        const arquivoDigitalService = await import('../../services/arquivoDigitalService');
        
        // Obter todos os arquivos digitais
        const arquivos = await arquivoDigitalService.listarArquivosDigitais();
        
        // Encontrar o arquivo correspondente a esta monografia
        const arquivo = arquivos.find(a => a.monografia_id === monografia.id);
        
        if (arquivo) {
          navigate(`/repositorio?id=${arquivo.id}`);
        } else {
          alert("Não foi possível encontrar o arquivo desta monografia no repositório");
        }
      } catch (error) {
        console.error("Erro ao acessar o repositório:", error);
        alert("Erro ao tentar acessar o repositório. Por favor, tente novamente.");
      }
    }}
    startIcon={<VisibilityIcon />}
  >
    Ver no Repositório
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };
  // ---------------------------
  // ABA "DEFESAS"
  // ---------------------------
  const renderDefesas = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Defesas Agendadas
        </Typography>
        <DefesaList />
      </CardContent>
    </Card>
  );

  // ---------------------------
  // ABA "AVALIAÇÕES"
  // ---------------------------
  const RenderAvaliacoes = () => {
    const [estatisticas, setEstatisticas] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
      const carregarEstatisticas = async () => {
        try {
          const data = await CientificoDashboardService.getEstatisticasAvaliacoes();
          setEstatisticas(data);
        } catch (err) {
          console.error('Erro ao carregar estatísticas:', err);
        }
      };
      carregarEstatisticas();
    }, []);

    if (!estatisticas) return <CircularProgress />;

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Avaliações de Defesas
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {estatisticas.total}
                </Typography>
                <Typography variant="body2">Total de Avaliações</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {estatisticas.estatisticas.concluidas}
                </Typography>
                <Typography variant="body2">Avaliações Concluídas</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {estatisticas.estatisticas.mediaGeral}
                </Typography>
                <Typography variant="body2">Média Geral</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Distribuição das Notas
            </Typography>
            {Object.entries(estatisticas.estatisticas?.distribuicaoNotas || {}).map(([faixa, quantidade]) => (
              <Box key={faixa} sx={{ mb: 1 }}>
                <Typography variant="body2" gutterBottom>
                  {faixa.charAt(0).toUpperCase() + faixa.slice(1)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(quantidade / estatisticas.total) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption">{quantidade} avaliações</Typography>
              </Box>
            ))}
          </Box>

          <Grid container spacing={3}>
            {estatisticas.avaliacoes?.map((avaliacao) => (
              <Grid item xs={12} sm={6} md={4} key={avaliacao.id}>
                <Card
                  elevation={3}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ArticleIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" color="primary.main">
                        Defesa {avaliacao.defesaId}
                      </Typography>
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                      Nota Final: {avaliacao.mediaFinal?.toFixed(2)}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Nota Arguente:</strong> {avaliacao.arguenteTotal?.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Nota Presidente:</strong> {avaliacao.presidenteTotal?.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Nota Supervisor:</strong> {avaliacao.supervisorTotal?.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                      Atualizado em: {new Date(avaliacao.updatedAt).toLocaleString()}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, mt: 'auto' }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/avaliacoes/${avaliacao.id}`)}
                    >
                      Ver Detalhes
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderAvaliacoes = () => <RenderAvaliacoes />;

  // ---------------------------
  // RENDER PRINCIPAL
  // ---------------------------
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Painel da Direção Científica
      </Typography>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<ArticleIcon />} label="Propostas" iconPosition="start" />
        <Tab icon={<AssignmentIcon />} label="Projetos" iconPosition="start" />
        <Tab icon={<DescriptionIcon />} label="Monografias" iconPosition="start" />
        <Tab icon={<EventNoteIcon />} label="Defesas" iconPosition="start" />
        <Tab icon={<VisibilityIcon />} label="Avaliações" iconPosition="start" />
        <Tab icon={<ArchiveIcon />} label="Arquivamento" iconPosition="start" />
      </Tabs>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={carregarDados}>
              Tentar Novamente
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && renderPropostas()}
          {activeTab === 1 && renderProjetos()}
          {activeTab === 2 && renderMonografias()}
          {activeTab === 3 && renderDefesas()}
          {activeTab === 4 && renderAvaliacoes()}
          {activeTab === 5 && <RenderArquivamento />}
        </>
      )}

      {/* Diálogos para Propostas e Projetos */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Rejeitar Proposta</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Motivo da Rejeição"
            fullWidth
            multiline
            rows={4}
            value={motivoRejeicao}
            onChange={(e) => setMotivoRejeicao(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleRejeitarProposta} color="error">
            Confirmar Rejeição
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={projetoDialogOpen} onClose={() => setProjetoDialogOpen(false)}>
        <DialogTitle>Aprovar Projeto</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Observações"
            fullWidth
            multiline
            rows={4}
            value={observacoesProjeto}
            onChange={(e) => setObservacoesProjeto(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjetoDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAprovarProjeto} color="success" variant="contained">
            Confirmar Aprovação
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Aprovar Proposta</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Observações"
            fullWidth
            multiline
            rows={4}
            value={observacoesProposta}
            onChange={(e) => setObservacoesProposta(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAprovarPropostaConfirm} color="success" variant="contained">
            Confirmar Aprovação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CientificoDashboardPage;
